import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";
import type { JobStatus, SearchPhase } from "@/types/api";

export interface RadarSpinnerProps {
  status: JobStatus;
  searchPhase?: SearchPhase;
  errorMessage?: string;
  /** Number of candidates scanned so far. Optional. */
  scanned?: number;
  /** Target scan count (denominator for scanned/target). Optional. */
  target?: number;
  /** Number of relevant companies found so far. Optional. */
  found?: number;
  /** Number of companies verified (Impressum etc.) so far. Optional. */
  verified?: number;
  /** Job reference shown in the readout (e.g. "A-0241"). Optional. */
  jobRef?: string;
}

type UiPhase =
  | "searching"
  | "enriching"
  | "scoring"
  | "expanding"
  | "done"
  | "error";

function resolvePhase(status: JobStatus, searchPhase?: SearchPhase): UiPhase {
  if (status === "failed" || status === "cancelled") return "error";
  if (searchPhase === "expanding") return "expanding";
  if (status === "completed") return "done";
  if (searchPhase === "enriching") return "enriching";
  if (searchPhase === "scoring") return "scoring";
  return "searching";
}

export default function RadarSpinner({
  status,
  searchPhase,
  errorMessage,
  scanned,
  target,
  found,
  verified,
  jobRef,
}: RadarSpinnerProps) {
  const { t } = useTranslation("search");
  const phase = resolvePhase(status, searchPhase);
  const isActive = phase !== "done" && phase !== "error";
  const prefersReducedMotion = usePrefersReducedMotion();
  const animated = isActive && !prefersReducedMotion;

  const heading =
    phase === "error"
      ? status === "cancelled"
        ? t("progressCard.heading.cancelled")
        : t("progressCard.heading.failed")
      : phase === "expanding"
        ? t("progressCard.heading.expanding")
        : t("progressCard.heading.running");

  const subtitle =
    phase === "error"
      ? undefined
      : phase === "expanding"
        ? t("progressCard.subtitle.expanding")
        : t(`progressCard.steps.${phase === "done" ? "done" : phase}`);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* ── Radar ────────────────────────────── */}
        <div
          className={cn(
            "relative w-full md:w-[240px] md:flex-shrink-0",
            "border-b md:border-b-0 md:border-r border-border",
            "bg-[radial-gradient(circle_at_50%_50%,hsl(var(--card))_0%,hsl(var(--background))_80%)]",
          )}
        >
          <RadarSvg animated={animated} ariaLabel={t("radar.ariaLabel")} />

          <div
            className={cn(
              "absolute top-3.5 left-3.5 right-3.5 flex justify-between",
              "font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground",
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-block h-1.5 w-1.5 rounded-full",
                  phase === "error" ? "bg-destructive" : "bg-success",
                  animated && "animate-pulse-soft",
                )}
                aria-hidden
              />
              {t(`radar.status.${phase}`)}
            </span>
            {jobRef && <span>#{jobRef}</span>}
          </div>
        </div>

        {/* ── Details ──────────────────────────── */}
        <div className="flex flex-1 flex-col px-6 py-6 md:px-7 md:py-7">
          <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {t("radar.scanLabel")}
          </div>

          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {heading}
          </h2>
          {subtitle && (
            <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
          )}

          {phase === "error" && errorMessage && (
            <p className="mt-4 text-sm text-destructive">{errorMessage}</p>
          )}

          <div
            className="mt-auto grid grid-cols-3 gap-3 pt-6"
            aria-live="polite"
          >
            <Stat
              label={t("radar.stats.scanned")}
              value={scanned}
              suffix={target !== undefined ? `/ ${target}` : undefined}
            />
            <Stat label={t("radar.stats.found")} value={found} accent />
            <Stat label={t("radar.stats.verified")} value={verified} />
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Radar SVG — concentric rings, cross-hairs, plotted dots, rotating
// sweep arm. Animations pause via prefers-reduced-motion in globals.
// ─────────────────────────────────────────────────────────────

const DOTS: ReadonlyArray<{ a: number; r: number }> = [
  { a: 38, r: 0.42 },
  { a: 112, r: 0.66 },
  { a: 176, r: 0.35 },
  { a: 208, r: 0.8 },
  { a: 260, r: 0.52 },
  { a: 292, r: 0.72 },
  { a: 324, r: 0.28 },
  { a: 15, r: 0.88 },
  { a: 82, r: 0.22 },
  { a: 145, r: 0.92 },
  { a: 240, r: 0.3 },
  { a: 350, r: 0.58 },
];

interface RadarSvgProps {
  animated: boolean;
  ariaLabel: string;
}

function RadarSvg({ animated, ariaLabel }: RadarSvgProps) {
  // useId() gives each RadarSvg instance a document-unique id so two
  // RadarSpinner instances on the same page don't collide on the global
  // SVG id namespace. Raw useId() output contains colons (e.g. ":r0:") —
  // safe for inline SVG attribute references like url(#...), NOT safe if
  // ever referenced from a CSS rule (escape or sanitize first).
  const gradientId = useId();
  return (
    <svg
      viewBox="0 0 240 340"
      className="block h-[220px] w-full md:h-[340px]"
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          <stop
            offset="100%"
            stopColor="hsl(var(--primary))"
            stopOpacity="0.55"
          />
        </linearGradient>
      </defs>

      {/* concentric rings */}
      {[0.25, 0.5, 0.75, 1].map((r, i) => (
        <circle
          key={i}
          cx="120"
          cy="170"
          r={100 * r}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeOpacity={0.14}
          strokeWidth={1}
        />
      ))}

      {/* cross-hairs */}
      <line
        x1="120"
        y1="70"
        x2="120"
        y2="270"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.1"
      />
      <line
        x1="20"
        y1="170"
        x2="220"
        y2="170"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.1"
      />

      {/* plotted dots (decorative — no live binding) */}
      {DOTS.map((d, i) => {
        const rad = ((d.a - 90) * Math.PI) / 180;
        const x = 120 + Math.cos(rad) * 100 * d.r;
        const y = 170 + Math.sin(rad) * 100 * d.r;
        return (
          <g key={i}>
            {animated && (
              <circle
                cx={x}
                cy={y}
                r={8}
                fill="hsl(var(--primary))"
                opacity={0.12}
              >
                <animate
                  attributeName="r"
                  from="3"
                  to="12"
                  dur="1.2s"
                  begin={`${(i * 0.1) % 1.2}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.35"
                  to="0"
                  dur="1.2s"
                  begin={`${(i * 0.1) % 1.2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <circle
              cx={x}
              cy={y}
              r={3}
              fill="hsl(var(--primary))"
              opacity={animated ? 1 : 0.4}
            />
          </g>
        );
      })}

      {/* sweep arm */}
      <g
        className={animated ? "radar-sweep-anim" : undefined}
        style={{ transformOrigin: "120px 170px" }}
      >
        <path
          d="M 120 170 L 120 70 A 100 100 0 0 1 215 140 Z"
          fill={`url(#${gradientId})`}
        />
        <line
          x1="120"
          y1="170"
          x2="120"
          y2="70"
          stroke="hsl(var(--primary))"
          strokeWidth={1.5}
        />
      </g>

      {/* center */}
      <circle cx="120" cy="170" r={4} fill="hsl(var(--primary))" />
      <circle
        cx="120"
        cy="170"
        r={10}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={1}
        opacity={0.4}
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Stat tile
// ─────────────────────────────────────────────────────────────

interface StatProps {
  label: string;
  value?: number;
  suffix?: string;
  accent?: boolean;
}

function Stat({ label, value, suffix, accent }: StatProps) {
  const display = typeof value === "number" ? String(value) : "—";
  return (
    <div className="border-t border-border pt-2.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div
        data-testid="stat-value"
        className={cn(
          "mt-1 truncate text-[22px] font-semibold leading-none tracking-tight tabular-nums",
          accent ? "text-primary" : "text-foreground",
        )}
      >
        {display}
        {suffix && (
          <span className="ml-1 text-[11px] font-medium text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
